import * as React from "react";
import * as S from "@styled";
import * as _ from "lodash";
import * as AppPath from "../model/AppPath";
import { CellContext, ColumnDef } from "@tanstack/react-table";

import { ListState, ProcessListColumns, optionalSubprocessListColumns, selectableProcessListColumns } from "../model/ListState";
import { Draft, ITriggerDescription, ProcessReferenceModel } from "../model/Process";
import { ProcessWithStatus } from "../model/ProcessWithStatus";

import { ProcessInstanceStateFilter } from "../model/ProcessInstanceStateFilter";
import { IndeterminateCheckbox } from "shared/components/Table";
import { getDraftEditLink, getEditLink } from "processList/model/AppLinks";
import { Utilities } from "shared/utilities";
import ActionMenu from "./ActionMenu";
import { ProcessColumnIdentifier, ProcessRow } from "processList/model/ProcessList";
import { ColumnVisibilityControl, ExecutionsPerDayCell, NameCell, TagCell } from "./ProcessListCell";
import { ProcessTriggersDisplay } from "./ProcessTriggersDisplay";
import { ProcessListArrayDisplay } from "./ProcessListArrayDisplay";
import { useParams } from "react-router";
import { IProcessListURL } from "processList/model/AppPath";
import styled from "styled-components";
import { State } from "processList/model/State";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import ProcessListTable from "./ProcessListTable";
import instance_running_icon from "../../shared/icons/instance_running.svg";
import Time from "shared/Time";
import { DispatchType } from "../../shared/utilities";
import { openColumnVisibilityControl, closeColumnVisibilityControl, setActionMenuOpenForProcess, removeActionMenuOpenForProcess } from "processList/actions/ProcessListActions";
import { maxVisibleColumns } from "processList/model/VisibleColumnsStore";
import { ProcessTemplateListQueryParams } from "processTemplates/model/AppPath";

const FailedThatAreNotAcknowledgedContainer = styled.div`
    width: 20px;
    height: 20px;
    background-color: ${S.Colors.warningPrimary};
    border-radius: 4px;
    color: ${S.Colors.white};
    text-align: center;
    font-size: 1rem;
`

function isProcessSelected(selectedProcesses: number[], processId: number): boolean {
    return selectedProcesses.some((id) => id === processId);
}

function isProcessDeploying(deployingProcesses: string[], processGuid: string): boolean {
    const guidLower = processGuid.toLowerCase();
    return deployingProcesses.some((guid) => guid.toLowerCase() === guidLower);
}

const ProcessInstanceListPathWrapper: React.FC<React.PropsWithChildren<IProcessInstanceListPathWrapperProps>> = (props) => {
    return (
        <Link
            className="process-list-cell-link-selector"
            to={AppPath.getInstancePath([props.processGuid], props.stateFilter)}>
            {props.children}
        </Link>
    )
}

const LogLevelRetentionHeader = (props: { title: string }) => (
    <div>{props.title} <i className="ci-info_circle_outline" title="Cell is empty if using Agent group value" /></div>
)

const ProcessListConnected: React.FC<ProcessListProps> = (props) => {

    const params = useParams<IProcessListURL>();
    const processListDependencies = [props, params];

    const replaceZeroValueWith = (value: string | number, replaceWith: string) => Number(value) !== 0 ? value : replaceWith;

    const columns = React.useMemo<Array<ColumnDef<ProcessRow>>>(
        () => [
            {
                id: 'select',
                header: ({ table }) => (
                    <IndeterminateCheckbox
                        {...{
                            id: "select-all-rows-selector",
                            checked: isAllProcessesSelected(),
                            indeterminate: table.getIsSomeRowsSelected(),
                            onChange: () => handleToggleSelectAll(),
                        }}
                    />
                ),
                cell: ({ row }) => (
                    <IndeterminateCheckbox
                        {...{
                            id: `select-${row.original.id}-selector`,
                            checked: isProcessSelected(props.selectedProcesses, row.original.id),
                            indeterminate: row.getIsSomeSelected(),
                            onChange: () => handleSelectProcess(row.original.id),
                        }}
                    />
                ),
            },
            {
                header: FRENDS.Localizations.Id,
                id: ProcessColumnIdentifier.id,
                accessorFn: acc => acc.id
            },
            {
                header: "GUID",
                id: ProcessColumnIdentifier.uniqueIdentifier,
                accessorFn: acc => acc.uniqueIdentifier
            },
            {
                id: ProcessColumnIdentifier.erroredNotAcknowledged,
                header: () => <FailedThatAreNotAcknowledgedContainer title="Failed that are not acknowledged"><span>!</span></FailedThatAreNotAcknowledgedContainer>,
                accessorFn: acc => acc.erroredNotAcknowledged,
                cell: (cell: CellContext<ProcessRow, string>) => (
                    <ProcessInstanceListPathWrapper
                        processGuid={cell.row.original.uniqueIdentifier}
                        stateFilter={ProcessInstanceStateFilter.ShowFailedNotAcknowledged}
                    >
                        <span style={{ color: S.Colors.warningPrimary }}>{replaceZeroValueWith(cell.getValue(), '')}</span>
                    </ProcessInstanceListPathWrapper>
                )
            },
            {
                header: FRENDS.Localizations.Name,
                id: ProcessColumnIdentifier.name,
                accessorFn: acc => acc.name,
                cell: (cell: CellContext<ProcessRow, string>) => (
                    <NameCell
                        {...cell}
                        possibleDraftId={getPossibleDraftId(cell.row.original.id)}
                        isDeploying={isProcessDeploying(props.deployingProcessGuids, cell.row.original.uniqueIdentifier)}
                        onShowChangeLogClick={props.onShowChangeLogClick}
                        isPinned={cell.row.original.isPinned}
                    />
                )
            },
            {
                header: "Target framework",
                id: ProcessColumnIdentifier.targetFramework,
                accessorFn: acc => acc.targetFramework
            },
            {
                header: FRENDS.Localizations.LatestExecutionStartTime,
                id: ProcessColumnIdentifier.latestExecutionStartTime,
                accessorFn: acc => acc.latestExecutionStartTime,
                cell: (cell: CellContext<ProcessRow, string>) => (
                    <ProcessInstanceListPathWrapper
                        processGuid={cell.row.original.uniqueIdentifier}
                    >
                        <span style={{ display: "flex", gap: "5px" }}>{Utilities.getIconFromExecutionState(cell.row.original.latestExecutionState, 20, null)} {cell.getValue()}</span>
                    </ProcessInstanceListPathWrapper>
                )
            },
            {
                header: "Template",
                id: ProcessColumnIdentifier.template,
                accessorFn: acc => acc.template,
                cell: ({ row }) => (
                    <>
                        {row.original.template && (
                            <a
                                href={`ProcessTemplates/${params.environmentName}/${params.agentGroupName}?${ProcessTemplateListQueryParams.highlightTemplate}=${row.original.template.uniqueIdentifier}`}
                                title="Show in Templates view"
                                className="process-template-list-link-selector"
                            >
                                {row.original.template.name}
                            </a>
                        )}
                    </>
                )
            },
            {
                header: "Frends version",
                id: ProcessColumnIdentifier.frendsVersion,
                accessorFn: acc => acc.frendsVersion
            },
            {
                header: "Tags",
                id: ProcessColumnIdentifier.tags,
                accessorFn: acc => acc.tags,
                cell: (cell: CellContext<ProcessRow, string[]>) => <TagCell cell={cell} />
            },
            {
                header: "Instance counts per day",
                id: ProcessColumnIdentifier.executionsPerDay,
                accessorFn: acc => acc.executionsPerDay,
                cell: (cell: CellContext<ProcessRow, number[]>) => (
                    <ProcessInstanceListPathWrapper
                        processGuid={cell.row.original.uniqueIdentifier}
                    >
                        <ExecutionsPerDayCell
                            {...cell}
                            executionsPerDay={cell.row.original.executionsPerDay}
                            countsTimeLimitDays={props.countsTimeLimitDays}
                        />
                    </ProcessInstanceListPathWrapper>
                )
            },
            {
                header: () => <img src={instance_running_icon} />,
                id: ProcessColumnIdentifier.running,
                accessorFn: acc => acc.running,
                cell: (cell: CellContext<ProcessRow, number>) => (
                    <ProcessInstanceListPathWrapper
                        processGuid={cell.row.original.uniqueIdentifier}
                        stateFilter={ProcessInstanceStateFilter.ShowRunning}
                    >
                        <span style={{ color: "#FFC40E" }}>{replaceZeroValueWith(cell.getValue(), '')}</span>
                    </ProcessInstanceListPathWrapper>
                )
            },
            {
                header: () => <i className="ci-circle_check_outline" style={{ color: S.Colors.uiGreen }} />,
                id: ProcessColumnIdentifier.successful,
                accessorFn: acc => acc.successful,
                cell: (cell: CellContext<ProcessRow, number>) => (
                    <ProcessInstanceListPathWrapper
                        processGuid={cell.row.original.uniqueIdentifier}
                        stateFilter={ProcessInstanceStateFilter.ShowSuccessful}
                    >
                        <span style={{ color: S.Colors.uiGreen }}>{replaceZeroValueWith(cell.getValue(), '')}</span>
                    </ProcessInstanceListPathWrapper>
                )
            },
            {
                header: () => <i className="ci-off_outline_close" style={{ color: S.Colors.warningPrimary }} />,
                id: ProcessColumnIdentifier.failed,
                accessorFn: acc => acc.failed,
                cell: (cell: CellContext<ProcessRow, number>) => (
                    <ProcessInstanceListPathWrapper
                        processGuid={cell.row.original.uniqueIdentifier}
                        stateFilter={ProcessInstanceStateFilter.ShowFailed}
                    >
                        <span style={{ color: S.Colors.warningPrimary }}>{replaceZeroValueWith(cell.getValue(), '')}</span>
                    </ProcessInstanceListPathWrapper>
                )
            },
            {
                header: FRENDS.Localizations.PromotedColumn,
                id: ProcessColumnIdentifier.promotedResultVariables,
                accessorFn: acc => acc.promotedResultVariables
            },
            {
                header: FRENDS.Localizations.Subprocesses,
                id: ProcessColumnIdentifier.usedProcessesList,
                accessorFn: acc => acc.usedProcessesList,
                cell: (cell: CellContext<ProcessRow, ProcessReferenceModel[][]>) => (
                    <ProcessListArrayDisplay
                        processReferences={cell.row.original.usedProcessesList}
                        processId={cell.row.original.id}
                        columName={ProcessColumnIdentifier.usedProcessesList}
                        popoverTitle={FRENDS.Localizations.Subprocesses}
                    />
                )
            },
            {
                header: FRENDS.Localizations.UsedByProcesses,
                id: ProcessColumnIdentifier.usedByProcessesList,
                accessorFn: acc => acc.usedByProcessesList,
                cell: (cell: CellContext<ProcessRow, ProcessReferenceModel[][]>) => (
                    <ProcessListArrayDisplay
                        processReferences={cell.row.original.usedByProcessesList}
                        processId={cell.row.original.id}
                        columName={ProcessColumnIdentifier.usedByProcessesList}
                        popoverTitle={FRENDS.Localizations.UsedByProcesses}
                    />
                )
            },
            {
                header: FRENDS.Localizations.Triggers,
                id: ProcessColumnIdentifier.triggers,
                accessorFn: acc => acc.triggers,
                cell: (cell: CellContext<ProcessRow, ITriggerDescription[]>) => (
                    <ProcessTriggersDisplay
                        processActive={cell.row.original.triggersActive}
                        processVersion={cell.row.original.version}
                        processId={cell.row.original.uniqueIdentifier}
                        triggers={cell.getValue()}
                    />
                )
            },
            {
                header: FRENDS.Localizations.Modified,
                id: ProcessColumnIdentifier.modified,
                accessorFn: acc => acc.modified
            },
            {
                header: FRENDS.Localizations.Modifier,
                id: ProcessColumnIdentifier.modifier,
                accessorFn: acc => acc.modifier
            },
            {
                header: () => <LogLevelRetentionHeader title={FRENDS.Localizations.LogLevel} />,
                id: ProcessColumnIdentifier.logLevel,
                accessorFn: acc => acc.logLevel,
                enableSorting: false
            },
            {
                header: () => <LogLevelRetentionHeader title="Log retention" />,
                id: ProcessColumnIdentifier.logRetention,
                accessorFn: acc => acc.logRetention,
                enableSorting: false
            },
            {
                id: ProcessColumnIdentifier.actions,
                accessorFn: acc => acc.actions,
                enableSorting: false,
                header: () => getColumnVisibilityControl(),
                cell: cell => (
                    <S.FlexContainer style={{ justifyContent: "flex-end", width: "100%", flexWrap: "nowrap" }} className="process-edit-action-container-selector">
                        <S.ButtonGroup gap={12}>
                            <S.RightSplitLinkButton href={getActionMenuEditLink(cell.row.original.id)} className="process-edit-button-selector">
                                {FRENDS.Localizations.Edit}
                            </S.RightSplitLinkButton>
                        </S.ButtonGroup>
                        <ActionMenu
                            managedState={getActionMenuState(cell.row.original.id)}
                            processIds={[cell.row.original.id]}
                            editLink={getActionMenuEditLink(cell.row.original.id)}
                            showProcessVariableEditItem={cell.row.original.processVariables?.length > 0}
                            labelComponent={
                                <S.LeftSplitButton className="process-actions-button-selector dropbtn" title="More actions" variant={S.ButtonType.solid}>
                                    <i className="ci-more_horizontal" />
                                </S.LeftSplitButton>
                            }
                        />
                    </S.FlexContainer>
                )
            }
        ],
        [props.selectedProcesses, props.deployingProcessGuids, props.countsTimeLimitDays, props.isSubprocessList, props.visibleColumns, props.listState]
    );

    const toggleSelectedProcesses = (selected: boolean, processIds: number[]) => {
        if (selected) {
            props.onSelectProcesses(processIds);
        } else {
            props.onDeselectProcesses(processIds);
        }
    };

    const isAllProcessesSelected = () => props.processes.every((f) => isProcessSelected(props.selectedProcesses, f.id));

    const handleToggleSelectAll = () => {
        props.onCloseColumnVisibilityControl();
        props.onRemoveActionMenuOpenForProcess();

        const processIds = props.processes && props.processes.map((p) => p.id) || [];
        toggleSelectedProcesses(!isAllProcessesSelected(), processIds);
    };

    const handleSelectProcess = (processId: number) => {
        props.onCloseColumnVisibilityControl();
        props.onRemoveActionMenuOpenForProcess();

        if (isProcessSelected(props.selectedProcesses, processId)) {
            props.onDeselectProcesses([processId]);
        } else {
            props.onSelectProcesses([processId]);
        }
    }

    const getPossibleDraft = (processId: number) => props.drafts && props.drafts.find((d) => d.processId === processId);
    const getPossibleDraftId = (processId: number) => {
        const possibleDraft = getPossibleDraft(processId);
        return possibleDraft && possibleDraft.id || null;
    }

    const handleColumnVisibilityControlOpen = () => {
        props.onRemoveActionMenuOpenForProcess();
        props.onOpenColumnVisibilityControl();
    };

    const handleToggleColumnVisibility = (column: keyof (ProcessWithStatus)) => {
        props.onCloseColumnVisibilityControl();
        props.onToggleVisibleColumn(column);
    };

    const getColumnVisibilityControl = () => (
        <div style={{ width: "100%" }}>
            <ColumnVisibilityControl
                open={props.listState.columnVisibilityControlOpen}
                onOpen={handleColumnVisibilityControlOpen}
                onHide={() => props.onCloseColumnVisibilityControl()}
                isSubprocessList={props.isSubprocessList}
                visibleColumns={props.visibleColumns}
                handleToggleColumn={handleToggleColumnVisibility}
                maxVisibleColumns={maxVisibleColumns}
                possibleColumns={props.isSubprocessList ? selectableProcessListColumns.concat(optionalSubprocessListColumns) : selectableProcessListColumns}
            />
        </div>
    );

    const handleActionMenuOpenForProcess = (processId: number) => {
        props.onCloseColumnVisibilityControl();
        props.onSetActionMenuOpenForProcess(processId);
    };

    const getActionMenuState = (processId: number) => {
        return {
            open: (props.listState.actionMenuOpenForProcess === processId),
            onOpen: () => handleActionMenuOpenForProcess(processId),
            onHide: () => props.onRemoveActionMenuOpenForProcess()
        }
    }

    const getActionMenuEditLink = (processId: number) => {
        const possibleDraftId = getPossibleDraftId(processId);
        return possibleDraftId ? getDraftEditLink(possibleDraftId) : getEditLink(processId);
    };

    const processes: ProcessRow[] = React.useMemo(() => props.processes.map((process, index): ProcessRow => ({
        id: process.id,
        uniqueIdentifier: process.uniqueIdentifier,
        targetFramework: process.targetFramework,
        latestExecutionState: process.latestExecutionState,
        erroredNotAcknowledged: process.erroredNotAcknowledged,
        name: process.name,
        latestExecutionStartTime: Time.toDetailedLocalTime(process.latestExecutionStartTime),
        frendsVersion: process.frendsVersion,
        tags: process.tags,
        executionsPerDay: process.executionsPerDay,
        running: process.running,
        successful: process.successful,
        failed: process.failed,
        promotedResultVariables: process.promotedResultVariables,
        usedByProcessesList: process.usedByProcessesList,
        usedProcessesList: process.usedProcessesList,
        triggers: process.triggers,
        modified: Time.toDetailedLocalTime(process.modified),
        modifier: process.modifier,
        majorVersion: process.majorVersion,
        minorVersion: process.minorVersion,
        version: process.version,
        description: process.description,
        isCrossPlatform: process.isCrossPlatform,
        isDotNet: process.isDotNet,
        triggersActive: process.triggersActive,
        uri: process.uri,
        usedTasks: process.usedTasks,
        isPinned: process.isPinned,
        processVariables: process.processVariables,
        template: process.template,
        logLevel: process.logLevel,
        logRetention: process.logRetention
    })), [processListDependencies]);

    return (
        <ProcessListTable
            columns={columns}
            data={processes}
            visibleColumns={props.visibleColumns}
            possibleColumns={ProcessListColumns}
            selectedProcesses={props.selectedProcesses}
            pageCount={props.pageCount}
            activePage={props.activePage}
            totalCountLabel={props.totalCountLabel}
        />
    );
};

interface IProcessInstanceListPathWrapperProps {
    stateFilter?: ProcessInstanceStateFilter;
    processGuid: string;
}


export interface ProcessListOwnProps {
    processes: ProcessWithStatus[];
    selectedProcesses: number[];
    visibleColumns: Array<keyof (ProcessWithStatus)>;
    drafts: Draft[];
    deployingProcessGuids: string[];
    countsTimeLimitDays: number;
    onSelectProcesses: (processIds: number[]) => void;
    onShowChangeLogClick: (processId: number) => void;
    onDeselectProcesses: (processIds: number[]) => void;
    onToggleVisibleColumn: (column: keyof (ProcessWithStatus)) => void;
    isSubprocessList: boolean;
    pageCount: number;
    activePage: number;
    totalCountLabel: string | null;
    agentGroupName: string;
}

export interface ProcessListDispatchProps {
    onOpenColumnVisibilityControl: () => void;
    onCloseColumnVisibilityControl: () => void;
    onSetActionMenuOpenForProcess: (processId: number) => void;
    onRemoveActionMenuOpenForProcess: () => void;
}

interface ProcessListStateProps {
    listState: ListState;
}

export type ProcessListProps = ProcessListOwnProps & ProcessListStateProps & ProcessListDispatchProps;

const mapStateToProps = (state: State): ProcessListStateProps => {
    return {
        listState: state.listState
    }
}

const mapDispatchToProps = (dispatch: DispatchType<State>): ProcessListDispatchProps => {
    return {
        onOpenColumnVisibilityControl: () => dispatch(openColumnVisibilityControl()),
        onCloseColumnVisibilityControl: () => dispatch(closeColumnVisibilityControl()),
        onSetActionMenuOpenForProcess: (processId: number) => dispatch(setActionMenuOpenForProcess(processId)),
        onRemoveActionMenuOpenForProcess: () => dispatch(removeActionMenuOpenForProcess()),
    };
};

export const ProcessList = connect(mapStateToProps, mapDispatchToProps)(ProcessListConnected);